import { HermesError, asHermesError } from "./errors";
import { parseEvent, EventType, type GatewayEvent, type ServerRequest, type ServerRequestReply } from "./events";
import { arr, bool, obj, str, type Json } from "./json";
import type { GatewaySocket } from "./transport";

export type ChannelOutput =
  | { kind: "event"; event: GatewayEvent }
  | { kind: "request"; request: ServerRequest }
  | { kind: "closed"; error: HermesError };

interface PendingCall {
  method: string;
  resolve: (v: Json) => void;
  reject: (e: unknown) => void;
  timer?: ReturnType<typeof setTimeout>;
}

/**
 * 一条 WebSocket 连接上的 JSON-RPC 通道（对应 HermesKit 的 RPCChannel）。
 * 每次重连都会新建一个通道，旧通道关闭时把所有未完成请求判失败。
 */
export class RPCChannel {
  static readonly defaultTimeout = 120;
  static readonly heartbeatInterval = 15;
  static readonly heartbeatDeadline = 45;

  private pending = new Map<string, PendingCall>();
  private nextID = 0;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private lastInbound = Date.now();
  private lastPingAt = 0;
  private answered = new Set<string>();
  private closed = false;

  constructor(private readonly socket: GatewaySocket, private readonly output: (o: ChannelOutput) => void) {}

  /** 开始读帧。连接建立后调用一次。 */
  start(): void {
    this.lastInbound = Date.now();
    void (async () => {
      while (!this.closed) {
        let text: string;
        try {
          text = await this.socket.receive();
        } catch (e) {
          this.shutdown(asHermesError(e));
          return;
        }
        this.handle(text);
      }
    })();
  }

  get isClosed(): boolean { return this.closed; }

  // MARK: 客户端 → 服务端请求

  call(method: string, params: Json = {}, timeout = RPCChannel.defaultTimeout, signal?: AbortSignal): Promise<Json> {
    if (this.closed) return Promise.reject(HermesError.notConnected());
    const id = `r${++this.nextID}`;
    const text = JSON.stringify({ jsonrpc: "2.0", id, method, params });
    return new Promise<Json>((resolve, reject) => {
      const call: PendingCall = { method, resolve, reject };
      if (timeout > 0) {
        call.timer = setTimeout(() => this.fail(id, HermesError.timeout(method, timeout)), timeout * 1000);
      }
      signal?.addEventListener("abort", () => this.fail(id, HermesError.cancelled()), { once: true });
      this.pending.set(id, call);
      this.socket.send(text).catch((e) => this.fail(id, asHermesError(e)));
    });
  }

  /** 发出请求但不等待结果 */
  fireAndForget(method: string, params: Json = {}): void {
    if (this.closed) return;
    const id = `n${++this.nextID}`;
    this.socket.send(JSON.stringify({ jsonrpc: "2.0", id, method, params })).catch(() => {});
  }

  /** 回答服务端请求。同一个 id 只回答一次（重连后补发的请求会复用 id）。 */
  respond(requestID: string, reply: ServerRequestReply): void {
    if (this.closed || this.answered.has(requestID)) return;
    this.answered.add(requestID);
    const frame = "result" in reply
      ? { jsonrpc: "2.0", id: requestID, result: reply.result ?? {} }
      : { jsonrpc: "2.0", id: requestID, error: reply.error };
    this.socket.send(JSON.stringify(frame)).catch(() => {});
  }

  close(): void {
    this.shutdown(HermesError.cancelled());
  }

  // MARK: 心跳

  /**
   * gateway.ready 里声明 heartbeat: true 时启动：每 15 秒 gateway.ping，45 秒没有任何入站帧判定断线。
   * 后台标签页的定时器可能被浏览器放慢到一分钟一次，所以只在“上一次 ping 之后一直没收到东西”时才判断线。
   */
  startHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.lastInbound = Date.now();
    this.lastPingAt = 0;
    let sequence = 0;
    this.heartbeatTimer = setInterval(() => {
      if (this.closed) return;
      const now = Date.now();
      const silentSincePing = this.lastPingAt > 0 && this.lastInbound < this.lastPingAt;
      if (silentSincePing && now - this.lastInbound >= RPCChannel.heartbeatDeadline * 1000 && now - this.lastPingAt >= 10000) {
        this.shutdown(HermesError.transport("心跳超时，连接可能已中断"));
        return;
      }
      sequence += 1;
      this.lastPingAt = now;
      this.socket.send(JSON.stringify({ jsonrpc: "2.0", id: `heartbeat-${sequence}`, method: "gateway.ping", params: {} }))
        .catch(() => this.shutdown(HermesError.transport("心跳发送失败")));
    }, RPCChannel.heartbeatInterval * 1000);
  }

  /** 回到前台时调用：马上发一次 ping，几秒内没回应就判断线，让上层立刻重连 */
  probe(): void {
    if (this.closed) return;
    const sentAt = Date.now();
    this.socket.send(JSON.stringify({ jsonrpc: "2.0", id: "heartbeat-probe", method: "gateway.ping", params: {} })).catch(() => {});
    setTimeout(() => {
      if (!this.closed && this.lastInbound < sentAt) this.shutdown(HermesError.transport("连接已中断"));
    }, 6000);
  }

  // MARK: 入站帧

  private handle(text: string): void {
    this.lastInbound = Date.now();
    let frame: Record<string, unknown> | undefined;
    try { frame = obj(JSON.parse(text)); } catch { return; }
    if (!frame) return;
    const id = frame.id;
    const method = str(frame.method);

    // 服务端 → 客户端请求：字符串 id + 非 event 的 method
    if (typeof id === "string" && method && method !== "event") {
      this.output({ kind: "request", request: { id, method, params: (frame.params ?? {}) as Json, replayed: false } });
      return;
    }

    if (id !== undefined && id !== null) {
      const key = String(id);
      if (key.startsWith("heartbeat-")) return;
      const call = this.pending.get(key);
      if (!call) return;
      this.pending.delete(key);
      if (call.timer) clearTimeout(call.timer);
      const err = obj(frame.error);
      if (err) {
        call.reject(HermesError.rpc(typeof err.code === "number" ? err.code : undefined,
          str(err.message) ?? `Hermes RPC 失败：${call.method}`, err.data));
      } else {
        const result = (frame.result ?? null) as Json;
        // 重连契约：session.resume / activate / events.since 的结果里带着仍在等待回答的服务端请求
        this.deliverOpenRequests(result);
        call.resolve(result);
      }
      return;
    }

    if (method === "event") {
      const event = parseEvent(frame.params);
      if (!event) return;
      if (event.type === EventType.gatewayReady && bool(obj(event.payload)?.heartbeat) === true) this.startHeartbeat();
      this.output({ kind: "event", event });
    }
  }

  private deliverOpenRequests(result: Json): void {
    const open = arr(obj(result)?.open_requests);
    if (!open) return;
    for (const entry of open) {
      const e = obj(entry);
      const id = str(e?.id);
      const method = str(e?.method);
      if (!id || !method) continue;
      this.output({ kind: "request", request: { id, method, params: (e!.params ?? {}) as Json, replayed: true } });
    }
  }

  private fail(id: string, error: HermesError): void {
    const call = this.pending.get(id);
    if (!call) return;
    this.pending.delete(id);
    if (call.timer) clearTimeout(call.timer);
    call.reject(error);
  }

  private shutdown(error: HermesError): void {
    if (this.closed) return;
    this.closed = true;
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    try { this.socket.close(); } catch { /* 已关闭 */ }
    const calls = [...this.pending.values()];
    this.pending.clear();
    for (const c of calls) { if (c.timer) clearTimeout(c.timer); c.reject(HermesError.notConnected()); }
    this.output({ kind: "closed", error });
  }
}
